import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';



import { Search, Filter, MapPin, Calendar, DollarSign, X } from 'lucide-react';

const SearchBar = ({ 
  onSearch, 
  onFilterChange, 
  filters = {}, 
  resultCount = 0,
  loading = false 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'conference', label: 'Conference' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'networking', label: 'Networking' },
    { value: 'concert', label: 'Concert' },
    { value: 'sports', label: 'Sports' },
    { value: 'exhibition', label: 'Exhibition' },
    { value: 'seminar', label: 'Seminar' },
    { value: 'meetup', label: 'Meetup' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'new-york', label: 'New York, NY' },
    { value: 'los-angeles', label: 'Los Angeles, CA' },
    { value: 'chicago', label: 'Chicago, IL' },
    { value: 'san-francisco', label: 'San Francisco, CA' },
    { value: 'miami', label: 'Miami, FL' },
    { value: 'austin', label: 'Austin, TX' },
    { value: 'seattle', label: 'Seattle, WA' }
  ];

  const priceRangeOptions = [
    { value: 'all', label: 'Any Price' },
    { value: '0-50', label: 'Under $50' },
    { value: '50-100', label: '$50 - $100' },
    { value: '100-200', label: '$100 - $200' },
    { value: '200-500', label: '$200 - $500' },
    { value: '500+', label: '$500+' }
  ];

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    onSearch?.(searchTerm);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      ...localFilters,
      [filterType]: value
    };
    setLocalFilters(newFilters);
    onFilterChange?.(filterType, value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    onSearch?.('');
  };

  const clearAllFilters = () => {
    const clearedFilters = {
      category: 'all',
      location: 'all',
      priceRange: 'all',
      dateRange: null
    };
    setLocalFilters(clearedFilters);
    setSearchTerm('');
    
    Object.keys(clearedFilters)?.forEach(key => {
      onFilterChange?.(key, clearedFilters?.[key]);
    });
    onSearch?.('');
  };

  const hasActiveFilters = 
    localFilters?.category !== 'all' ||
    localFilters?.location !== 'all' ||
    localFilters?.priceRange !== 'all' ||
    localFilters?.dateRange ||
    searchTerm?.length > 0;

  return (
    <div className="bg-card border-b border-border">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Main Search Bar */}
        <form onSubmit={handleSearchSubmit} className="mb-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-muted-foreground" />
            </div>
            <input
              type="text"
              placeholder="Search events, locations, or organizers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              disabled={loading}
              className="w-full pl-12 pr-12 py-4 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute inset-y-0 right-12 flex items-center pr-2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`
                absolute inset-y-0 right-0 flex items-center pr-4 transition-colors
                ${hasActiveFilters 
                  ? 'text-primary' :'text-muted-foreground hover:text-foreground'
                }
              `}
            >
              <Filter className="h-5 w-5" />
            </button>
          </div>
        </form>

        {/* Quick Search Actions */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleSearchSubmit({ preventDefault: () => {} })}
              disabled={loading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
            
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors text-sm font-medium"
              >
                Clear All
              </button>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            {loading ? (
              'Searching...'
            ) : (
              `${resultCount} event${resultCount !== 1 ? 's' : ''} found`
            )}
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="bg-background border border-border rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Advanced Filters</h3>
              <button
                onClick={() => setShowFilters(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Location Filter */}
              <div>
                <label className="flex items-center text-sm font-medium text-foreground mb-2">
                  <MapPin className="w-4 h-4 mr-2 text-primary" />
                  Location
                </label>
                <select
                  value={localFilters?.location || 'all'}
                  onChange={(e) => handleFilterChange('location', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Locations</option>
                  <option value="new-york">New York</option>
                  <option value="san-francisco">San Francisco</option>
                  <option value="los-angeles">Los Angeles</option>
                  <option value="chicago">Chicago</option>
                  <option value="miami">Miami</option>
                  <option value="austin">Austin</option>
                  <option value="seattle">Seattle</option>
                  <option value="online">Online</option>
                </select>
              </div>

              {/* Price Range Filter */}
              <div>
                <label className="flex items-center text-sm font-medium text-foreground mb-2">
                  <DollarSign className="w-4 h-4 mr-2 text-primary" />
                  Price Range
                </label>
                <select
                  value={localFilters?.priceRange || 'all'}
                  onChange={(e) => handleFilterChange('priceRange', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Prices</option>
                  <option value="0-0">Free</option>
                  <option value="1-50">$1 - $50</option>
                  <option value="51-100">$51 - $100</option>
                  <option value="101-200">$101 - $200</option>
                  <option value="201-500">$201 - $500</option>
                  <option value="500+">$500+</option>
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label className="flex items-center text-sm font-medium text-foreground mb-2">
                  <Calendar className="w-4 h-4 mr-2 text-primary" />
                  Date
                </label>
                <input
                  type="date"
                  value={localFilters?.dateRange?.from || ''}
                  onChange={(e) => handleFilterChange('dateRange', e?.target?.value ? { from: e?.target?.value } : null)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-6 pt-4 border-t border-border">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-foreground">Active filters:</span>
                  
                  {searchTerm && (
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs">
                      Search: "{searchTerm}"
                    </span>
                  )}

                  {localFilters?.category !== 'all' && (
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs capitalize">
                      {localFilters?.category}
                    </span>
                  )}

                  {localFilters?.location !== 'all' && (
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs">
                      {localFilters?.location?.replace('-', ' ')}
                    </span>
                  )}

                  {localFilters?.priceRange !== 'all' && (
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs">
                      {localFilters?.priceRange === '0-0' ? 'Free' : `$${localFilters?.priceRange}`}
                    </span>
                  )}

                  {localFilters?.dateRange?.from && (
                    <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs">
                      After: {localFilters?.dateRange?.from}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const FilterChip = ({ label, onRemove }) => {
  return (
    <div className="flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
      <span>{label}</span>
      <button
        onClick={onRemove}
        className="hover:bg-primary/20 rounded-full p-0.5 transition-smooth"
      >
        <Icon name="X" size={14} />
      </button>
    </div>
  );
};

export default SearchBar;